import React, { useCallback, useMemo } from 'react';
import { useField, useFormState } from 'react-final-form';
import { CheckboxField } from 'ksnm-common-ui/lib/Form/CheckboxField';
import type { CheckboxFieldProps } from 'ksnm-common-ui/lib/Form/CheckboxField/CheckboxFormField';

import { CheckboxWithRelatedFields, EMPTY_PASSWORD_TYPE_VALUE, MailKeys } from '@stores/settingsStore/constants';
import type { PropertyWithType } from '@stores/settingsStore';

import { useFieldMail } from './hooks';

interface ICheckboxFieldWrapper extends CheckboxFieldProps {
  keyVal: string;
  fieldNamesByCheckbox: Record<string, string>;
  name: string;
}

export const CheckboxFieldWrapper = ({ fieldNamesByCheckbox, ...rest }: ICheckboxFieldWrapper): React.ReactElement => {
  const { initialValues } = useFormState();

  const {
    input: { value },
  } = useField(rest.name);

  const findInitialValues = useCallback(
    (needKeyField: string) => initialValues?.properties?.find((val: PropertyWithType) => val.key === needKeyField),
    [initialValues?.properties],
  );

  // Почта
  const {
    onChangeMailHost,
    onChangeMailPort,
    onChangeMailUserName,
    onChangeMailPassword,
    onChangeMailAuthEnable,
    onChangeMailStarttlsEnable,
    onChangeMailSslEnable,
    onChangeMailSenderName,
    onChangeMailUrl,
    onChangeTransportProtocol,
  } = useFieldMail(fieldNamesByCheckbox);

  const onChangeMail = useCallback(
    (v) => {
      if (rest?.keyVal === CheckboxWithRelatedFields.mail) {
        const mailHostValues = findInitialValues(MailKeys.host);
        const mailPortValues = findInitialValues(MailKeys.port);
        const mailUserNameValues = findInitialValues(MailKeys.username);
        const mailAuthEnableValues = findInitialValues(MailKeys.authEnable);
        const mailStarttlsEnableValues = findInitialValues(MailKeys.starttlsEnable);
        const mailSslEnableValues = findInitialValues(MailKeys.sslEnable);
        const mailSenderNameValues = findInitialValues(MailKeys.senderName);
        const mailUrlValues = findInitialValues(MailKeys.url);
        const transportProtocolValues = findInitialValues(MailKeys.transportProtocol);

        onChangeMailHost(v ? mailHostValues?.value : null);
        onChangeMailPort(v ? mailPortValues?.value : null);
        onChangeMailUserName(v ? mailUserNameValues?.value : null);
        onChangeMailPassword(EMPTY_PASSWORD_TYPE_VALUE);
        onChangeMailAuthEnable(v ? mailAuthEnableValues?.value : null);
        onChangeMailStarttlsEnable(v ? mailStarttlsEnableValues?.value : null);
        onChangeMailSslEnable(v ? mailSslEnableValues?.value : null);
        onChangeMailSenderName(v ? mailSenderNameValues?.value : null);
        onChangeMailUrl(v ? mailUrlValues?.value : null);
        onChangeTransportProtocol(v ? transportProtocolValues?.value : null);
      }
    },
    [
      findInitialValues,
      onChangeMailAuthEnable,
      onChangeMailHost,
      onChangeMailPassword,
      onChangeMailPort,
      onChangeMailSenderName,
      onChangeMailSslEnable,
      onChangeMailStarttlsEnable,
      onChangeMailUrl,
      onChangeMailUserName,
      onChangeTransportProtocol,
      rest?.keyVal,
    ],
  );

  const onchange = useCallback(
    (v) => {
      // При отключении чекбокса значения сбрасываются на дефолтные (то есть null), при включении возвращаются на initialValues
      onChangeMail(v); // Почта
    },
    [onChangeMail],
  );

  const needConfirm = useMemo(
    () => !!value && Object.values(CheckboxWithRelatedFields).includes(`${rest?.keyVal}` as CheckboxWithRelatedFields),
    [rest?.keyVal, value],
  );

  return (
    <CheckboxField
      {...rest}
      onChange={onchange}
      needConfirm={needConfirm}
      confirmText="После сохранения настроек указанные параметры будут сброшены на дефолтные"
    />
  );
};
